import React, { useEffect, useRef, useState, useCallback } from 'react';
import { formatPrice } from '../../utils/formatters';

function valueColor(isPositive) {
  return isPositive ? '#10b981' : '#ef4444';
}

function useFlash(prices, symbols) {
  const prevRef = useRef({});
  const [flash, setFlash] = useState({});

  useEffect(() => {
    const updates = {};
    for (const sym of symbols) {
      const cur = prices?.[sym]?.price;
      const prev = prevRef.current[sym];
      if (cur != null && prev != null && cur !== prev) {
        updates[sym] = cur > prev ? 'up' : 'down';
      }
      if (cur != null) prevRef.current[sym] = cur;
    }
    if (Object.keys(updates).length === 0) return;
    setFlash((prev) => ({ ...prev, ...updates }));
    const t = setTimeout(() => setFlash((prev) => {
      const next = { ...prev };
      for (const sym of Object.keys(updates)) delete next[sym];
      return next;
    }), 250);
    return () => clearTimeout(t);
  });

  return flash;
}

function OptionsSkeleton({ colors }) {
  const skeletonRow = (i) => (
    <div key={i} className="rounded-xl p-2 animate-pulse"
      style={{ background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="h-3.5 w-12 rounded" style={{ backgroundColor: `${colors.accent}20` }} />
        <div className="h-2.5 w-8 rounded" style={{ backgroundColor: `${colors.accent}15` }} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg p-2 space-y-1.5" style={{ backgroundColor: colors.inputBg }}>
          <div className="flex justify-between">
            <div className="h-2.5 w-10 rounded" style={{ backgroundColor: `${colors.accent}18` }} />
            <div className="h-2.5 w-12 rounded" style={{ backgroundColor: `${colors.accent}18` }} />
          </div>
          <div className="h-7 rounded-lg" style={{ backgroundColor: `${colors.accent}12` }} />
        </div>
        <div className="rounded-lg p-2 space-y-1.5" style={{ backgroundColor: colors.inputBg }}>
          <div className="flex justify-between">
            <div className="h-2.5 w-10 rounded" style={{ backgroundColor: `${colors.accent}18` }} />
            <div className="h-2.5 w-12 rounded" style={{ backgroundColor: `${colors.accent}18` }} />
          </div>
          <div className="h-7 rounded-lg" style={{ backgroundColor: `${colors.accent}12` }} />
        </div>
      </div>
    </div>
  );
  return <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => skeletonRow(i))}</div>;
}

export default function OptionsTable({ chain, colors, onTrade, prices = {}, loading = false, chainLoading = false }) {
  const allSymbols = chain?.chain?.flatMap((r) => [r.call?.symbol, r.put?.symbol].filter(Boolean)) ?? [];
  const flash = useFlash(prices, allSymbols);
  const [expandedStrike, setExpandedStrike] = useState(null);
  const toggleExpand = useCallback((strike) => setExpandedStrike((prev) => (prev === strike ? null : strike)), []);
  const atmRef = useRef(null);
  const containerRef = useRef(null);
  const [showAtmChip, setShowAtmChip] = useState(false);

  const scrollToAtm = useCallback(() => {
    atmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Auto-scroll to ATM on chain load, show chip when ATM is out of view
  useEffect(() => {
    if (!chain?.chain?.length) return;
    const t = setTimeout(() => atmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    return () => clearTimeout(t);
  }, [chain?.expiry, chain?.underlying]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const atm = atmRef.current;
    if (!atm) return;
    const obs = new IntersectionObserver(([entry]) => setShowAtmChip(!entry.isIntersecting), { threshold: 0.5 });
    obs.observe(atm);
    return () => obs.disconnect();
  }, [chain?.chain?.length]);

  if (loading && !chain?.chain?.length) {
    return <OptionsSkeleton colors={colors} />;
  }

  if (!chain?.chain?.length) {
    return (
      <div
        className="rounded-xl p-4 text-sm text-center"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}`, color: colors.secondaryText }}
      >
        No option contracts found for this expiry.
      </div>
    );
  }

  const rows = chain.chain.map((row) => {
    const zone = row.is_atm ? 'ATM' : chain.spot && row.strike < chain.spot ? 'ITM' : 'OTM';
    const callLive = row.call?.symbol ? prices?.[row.call.symbol] : null;
    const putLive = row.put?.symbol ? prices?.[row.put.symbol] : null;
    const callLtp = callLive?.price ?? row.call?.ltp;
    const putLtp = putLive?.price ?? row.put?.ltp;
    const callChg = callLive?.change_pct ?? row.call?.change_pct;
    const putChg = putLive?.change_pct ?? row.put?.change_pct;
    const callVol = callLive?.volume ?? row.call?.volume;
    const putVol = putLive?.volume ?? row.put?.volume;
    const callUp = (callChg ?? 0) >= 0;
    const putUp = (putChg ?? 0) >= 0;
    const callFlash = row.call?.symbol ? flash[row.call.symbol] : null;
    const putFlash = row.put?.symbol ? flash[row.put.symbol] : null;
    return { ...row, zone, callLtp, putLtp, callChg, putChg, callVol, putVol, callUp, putUp, callFlash, putFlash };
  });

  const btnBuy = {
    background: 'linear-gradient(135deg, #10b98122 0%, #10b98110 100%)',
    color: '#10b981',
    border: '1px solid #10b98140',
    boxShadow: '0 1px 4px #10b98118',
  };
  const btnSell = {
    background: 'linear-gradient(135deg, #ef444422 0%, #ef444410 100%)',
    color: '#ef4444',
    border: '1px solid #ef444440',
    boxShadow: '0 1px 4px #ef444418',
  };

  const tableContent = (
    <div className="space-y-2">

      {/* ── Mobile: Option C compact table ── */}
      <div className="md:hidden relative" ref={containerRef}>

        {/* Sticky ATM chip — appears when ATM row scrolls out of view */}
        {showAtmChip && (
          <button
            onClick={scrollToAtm}
            className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              color: '#fff',
              boxShadow: '0 2px 12px #8b5cf640',
            }}>
            ◎ ATM
          </button>
        )}

      <div className="rounded-2xl overflow-hidden"
        style={{
          border: `1px solid ${colors.border}`,
          boxShadow: `0 4px 24px ${colors.accent}10`,
        }}>

        {/* Header */}
        <div className="grid grid-cols-3 py-2.5"
          style={{
            background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}06 100%)`,
            borderBottom: `1px solid ${colors.border}`,
          }}>
          <div className="flex items-center gap-1 px-4 border-r" style={{ borderColor: colors.border }}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 opacity-80" />
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#10b981cc' }}>CE</p>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-center self-center" style={{ color: colors.secondaryText }}>Strike</p>
          <div className="flex items-center justify-end gap-1 px-4 border-l" style={{ borderColor: colors.border }}>
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#ef4444cc' }}>PE</p>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 opacity-80" />
          </div>
        </div>

        {rows.map((row) => {
          const isExpanded = expandedStrike === row.strike;
          const zoneColor = row.is_atm ? '#8b5cf6' : row.zone === 'ITM' ? '#10b981' : '#eab308';
          const atmGlow = row.is_atm ? `0 0 0 1px #8b5cf640, 0 2px 12px #8b5cf620` : 'none';
          const rowBg = row.is_atm
            ? 'linear-gradient(135deg, #8b5cf614 0%, #8b5cf608 100%)'
            : row.zone === 'ITM'
              ? 'rgba(16,185,129,0.06)'
              : 'rgba(234,179,8,0.04)';

          return (
            <div key={row.strike}
              ref={row.is_atm ? atmRef : null}
              style={{
                borderBottom: `1px solid ${colors.border}`,
                boxShadow: atmGlow,
                position: 'relative',
              }}>

              {/* ATM left accent bar */}
              {row.is_atm && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 3, background: 'linear-gradient(180deg, #8b5cf6 0%, #6d28d9 100%)',
                  borderRadius: '0 2px 2px 0',
                }} />
              )}

              {/* Compact row */}
              <button
                className="w-full grid grid-cols-3 items-center py-3 text-left transition-opacity active:opacity-60"
                style={{ background: rowBg }}
                onClick={() => toggleExpand(row.strike)}
              >
                {/* CE side */}
                <div className="px-4 border-r" style={{ borderColor: `${colors.border}80` }}>
                  <span className="text-[13px] font-bold tabular-nums leading-tight inline-block rounded px-0.5"
                    style={{
                      color: valueColor(row.callUp),
                      backgroundColor: 'transparent',
                      transition: 'none',
                    }}>
                    {row.callLtp != null ? formatPrice(row.callLtp) : '—'}
                  </span>
                  {row.callChg != null && (
                    <p className="text-[10px] font-semibold mt-0.5" style={{ color: valueColor(row.callUp) }}>
                      {row.callUp ? '▲' : '▼'} {Math.abs(row.callChg).toFixed(2)}%
                    </p>
                  )}
                </div>

                {/* Strike */}
                <div className="text-center px-2">
                  <p className="text-[13px] font-extrabold tabular-nums" style={{ color: zoneColor }}>
                    {row.strike.toLocaleString('en-IN')}
                  </p>
                  <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5"
                    style={{
                      backgroundColor: `${zoneColor}18`,
                      color: zoneColor,
                      letterSpacing: '0.04em',
                    }}>
                    {row.is_atm ? 'ATM' : row.zone}
                  </span>
                </div>

                {/* PE side */}
                <div className="text-right px-4 border-l" style={{ borderColor: `${colors.border}80` }}>
                  <span className="text-[13px] font-bold tabular-nums leading-tight inline-block rounded px-0.5"
                    style={{
                      color: row.putFlash === 'up' ? '#10b981' : row.putFlash === 'down' ? '#ef4444' : valueColor(row.putUp),
                      backgroundColor: row.putFlash === 'up' ? 'rgba(16,185,129,0.16)' : row.putFlash === 'down' ? 'rgba(239,68,68,0.16)' : 'transparent',
                      transition: 'background-color 0.3s, color 0.3s',
                    }}>
                    {row.putLtp != null ? formatPrice(row.putLtp) : '—'}
                  </span>
                  {row.putChg != null && (
                    <p className="text-[10px] font-semibold mt-0.5" style={{ color: valueColor(row.putUp) }}>
                      {row.putUp ? '▲' : '▼'} {Math.abs(row.putChg).toFixed(2)}%
                    </p>
                  )}
                </div>
              </button>

              {/* Expanded panel */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2"
                  style={{ borderTop: `1px solid ${colors.border}50`, background: `${colors.cardBg}88` }}>

                  {/* CE detail */}
                  <div className="rounded-xl p-2.5 space-y-2"
                    style={{
                      background: 'linear-gradient(135deg, #10b98112 0%, #10b98106 100%)',
                      border: '1px solid #10b98128',
                    }}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-bold" style={{ color: '#10b981' }}>Call (CE)</p>
                      {row.callVol != null && (
                        <p className="text-[9px]" style={{ color: colors.secondaryText }}>
                          Vol {Number(row.callVol).toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => row.call && onTrade({ ...row.call, type: 'CE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'BUY', ltp: row.callLtp })}
                        disabled={!row.call}
                        className="py-2 rounded-lg text-[11px] font-bold disabled:opacity-40 transition-opacity active:opacity-70"
                        style={btnBuy}
                      >
                        Buy
                      </button>
                      <button
                        onClick={() => row.call && onTrade({ ...row.call, type: 'CE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'SELL', ltp: row.callLtp })}
                        disabled={!row.call}
                        className="py-2 rounded-lg text-[11px] font-bold disabled:opacity-40 transition-opacity active:opacity-70"
                        style={btnSell}
                      >
                        Sell
                      </button>
                    </div>
                  </div>

                  {/* PE detail */}
                  <div className="rounded-xl p-2.5 space-y-2"
                    style={{
                      background: 'linear-gradient(135deg, #ef444412 0%, #ef444406 100%)',
                      border: '1px solid #ef444428',
                    }}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-bold" style={{ color: '#ef4444' }}>Put (PE)</p>
                      {row.putVol != null && (
                        <p className="text-[9px]" style={{ color: colors.secondaryText }}>
                          Vol {Number(row.putVol).toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => row.put && onTrade({ ...row.put, type: 'PE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'BUY', ltp: row.putLtp })}
                        disabled={!row.put}
                        className="py-2 rounded-lg text-[11px] font-bold disabled:opacity-40 transition-opacity active:opacity-70"
                        style={btnBuy}
                      >
                        Buy
                      </button>
                      <button
                        onClick={() => row.put && onTrade({ ...row.put, type: 'PE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'SELL', ltp: row.putLtp })}
                        disabled={!row.put}
                        className="py-2 rounded-lg text-[11px] font-bold disabled:opacity-40 transition-opacity active:opacity-70"
                        style={btnSell}
                      >
                        Sell
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>

      {/* ── Desktop table ── */}
      <div className="hidden md:block overflow-x-auto rounded-xl"
        style={{
          border: `1px solid ${colors.border}`,
          boxShadow: `0 4px 24px ${colors.accent}08`,
          WebkitOverflowScrolling: 'touch',
        }}>
        <table className="w-full border-collapse" style={{ minWidth: 760 }}>
          <thead>
            <tr style={{
              borderBottom: `1px solid ${colors.border}`,
              background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
            }}>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-right whitespace-nowrap" style={{ color: '#ef4444aa' }}>CE Sell</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-right whitespace-nowrap" style={{ color: '#10b981aa' }}>CE Buy</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-right whitespace-nowrap" style={{ color: colors.secondaryText }}>CE LTP</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-right whitespace-nowrap" style={{ color: colors.secondaryText }}>CE Chg%</th>
              <th className="hidden sm:table-cell px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-right whitespace-nowrap" style={{ color: colors.secondaryText }}>CE Vol</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-center whitespace-nowrap" style={{ color: colors.primaryText }}>Strike</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-center whitespace-nowrap" style={{ color: colors.secondaryText }}>Zone</th>
              <th className="hidden sm:table-cell px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-left whitespace-nowrap" style={{ color: colors.secondaryText }}>PE Vol</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-left whitespace-nowrap" style={{ color: colors.secondaryText }}>PE LTP</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-left whitespace-nowrap" style={{ color: colors.secondaryText }}>PE Chg%</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-left whitespace-nowrap" style={{ color: '#10b981aa' }}>PE Buy</th>
              <th className="px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-left whitespace-nowrap" style={{ color: '#ef4444aa' }}>PE Sell</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.strike}
                style={{
                  borderBottom: `1px solid ${colors.border}50`,
                  background: row.is_atm
                    ? 'linear-gradient(135deg, #8b5cf614 0%, #8b5cf606 100%)'
                    : row.zone === 'ITM'
                      ? 'rgba(16,185,129,0.06)'
                      : 'rgba(234,179,8,0.04)',
                  transition: 'background 0.2s',
                }}>
                <td className="px-2 py-1.5 text-right">
                  <button onClick={() => row.call && onTrade({ ...row.call, type: 'CE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'SELL', ltp: row.callLtp })} disabled={!row.call} className="px-2 py-1 rounded-lg text-[10px] font-bold disabled:opacity-40 whitespace-nowrap transition-opacity active:opacity-60" style={btnSell}>Sell</button>
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button onClick={() => row.call && onTrade({ ...row.call, type: 'CE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'BUY', ltp: row.callLtp })} disabled={!row.call} className="px-2 py-1 rounded-lg text-[10px] font-bold disabled:opacity-40 whitespace-nowrap transition-opacity active:opacity-60" style={btnBuy}>Buy</button>
                </td>
                <td className="px-2 py-1.5 text-xs text-right font-bold tabular-nums whitespace-nowrap"
                  style={{
                    color: row.callFlash === 'up' ? '#10b981' : row.callFlash === 'down' ? '#ef4444' : valueColor(row.callUp),
                    backgroundColor: row.callFlash === 'up' ? 'rgba(16,185,129,0.2)' : row.callFlash === 'down' ? 'rgba(239,68,68,0.2)' : 'transparent',
                    transition: 'background-color 0.3s, color 0.3s',
                  }}>
                  {row.callLtp != null ? formatPrice(row.callLtp) : '—'}
                </td>
                <td className="px-2 py-1.5 text-xs text-right font-semibold whitespace-nowrap" style={{ color: row.callChg == null ? colors.secondaryText : valueColor(row.callUp) }}>
                  {row.callChg != null ? `${row.callUp ? '▲' : '▼'} ${Math.abs(row.callChg).toFixed(2)}%` : '—'}
                </td>
                <td className="hidden sm:table-cell px-2 py-1.5 text-xs text-right whitespace-nowrap" style={{ color: colors.secondaryText }}>
                  {row.callVol != null ? Number(row.callVol).toLocaleString('en-IN') : '—'}
                </td>
                <td className="px-2 py-1.5 text-xs text-center font-extrabold tabular-nums whitespace-nowrap"
                  style={{ color: row.is_atm ? '#8b5cf6' : colors.primaryText }}>
                  {row.strike.toLocaleString('en-IN')}
                  {row.is_atm && (
                    <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full align-middle font-bold"
                      style={{ backgroundColor: '#8b5cf622', color: '#8b5cf6' }}>ATM</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-xs text-center font-bold whitespace-nowrap">
                  <span className="px-2 py-0.5 rounded-full text-[10px]"
                    style={{
                      backgroundColor: row.is_atm ? '#8b5cf620' : row.zone === 'ITM' ? '#10b98118' : '#eab30814',
                      color: row.is_atm ? '#8b5cf6' : row.zone === 'ITM' ? '#10b981' : '#eab308',
                    }}>
                    {row.zone}
                  </span>
                </td>
                <td className="hidden sm:table-cell px-2 py-1.5 text-xs text-left whitespace-nowrap" style={{ color: colors.secondaryText }}>
                  {row.putVol != null ? Number(row.putVol).toLocaleString('en-IN') : '—'}
                </td>
                <td className="px-2 py-1.5 text-xs text-left font-bold tabular-nums whitespace-nowrap"
                  style={{
                    color: row.putFlash === 'up' ? '#10b981' : row.putFlash === 'down' ? '#ef4444' : valueColor(row.putUp),
                    backgroundColor: row.putFlash === 'up' ? 'rgba(16,185,129,0.2)' : row.putFlash === 'down' ? 'rgba(239,68,68,0.2)' : 'transparent',
                    transition: 'background-color 0.3s, color 0.3s',
                  }}>
                  {row.putLtp != null ? formatPrice(row.putLtp) : '—'}
                </td>
                <td className="px-2 py-1.5 text-xs text-left font-semibold whitespace-nowrap" style={{ color: row.putChg == null ? colors.secondaryText : valueColor(row.putUp) }}>
                  {row.putChg != null ? `${row.putUp ? '▲' : '▼'} ${Math.abs(row.putChg).toFixed(2)}%` : '—'}
                </td>
                <td className="px-2 py-1.5 text-left">
                  <button onClick={() => row.put && onTrade({ ...row.put, type: 'PE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'BUY', ltp: row.putLtp })} disabled={!row.put} className="px-2 py-1 rounded-lg text-[10px] font-bold disabled:opacity-40 whitespace-nowrap transition-opacity active:opacity-60" style={btnBuy}>Buy</button>
                </td>
                <td className="px-2 py-1.5 text-left">
                  <button onClick={() => row.put && onTrade({ ...row.put, type: 'PE', underlying: chain.underlying, expiry: chain.expiry, strike: row.strike, side: 'SELL', ltp: row.putLtp })} disabled={!row.put} className="px-2 py-1 rounded-lg text-[10px] font-bold disabled:opacity-40 whitespace-nowrap transition-opacity active:opacity-60" style={btnSell}>Sell</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (chainLoading) {
    return (
      <div className="relative">
        <div className="pointer-events-none" style={{ opacity: 0.4 }}>{tableContent}</div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl"
          style={{ backgroundColor: `${colors.cardBg}cc` }}>
          <div className="w-5 h-5 rounded-full border-2 animate-spin"
            style={{ borderColor: `${colors.accent}80`, borderTopColor: 'transparent' }} />
          <p className="text-[11px] font-medium" style={{ color: colors.secondaryText }}>Updating…</p>
        </div>
      </div>
    );
  }

  return tableContent;
}
